import { forwardRef } from 'react';

/**
 * Shared CTA button — use for labeled actions only (Save, Submit, Cancel, Delete).
 * Icon toggles, close/X, nav, tabs, and pagination stay as native <button>s.
 *
 * size="lg" → 24px label + arrow (auth screens)
 * size="md" → default compact CTA
 * size="sm" → compact table/inline action CTA (~32px height, 12px padding, 13px font)
 * icon + iconPosition — default end (Text then Icon)
 */
export const Button = forwardRef(function Button(
  {
    children,
    variant = 'primary',
    size = 'md',
    className = '',
    icon,
    iconPosition = 'end',
    ...props
  },
  ref,
) {
  const baseClasses =
    'inline-flex items-center justify-center font-sf-pro-display font-medium rounded-[10px] gap-[6px] transition-all duration-200 focus:outline-none outline-none whitespace-nowrap disabled:opacity-70 disabled:cursor-not-allowed';

  const sizeClasses =
    size === 'lg'
      ? 'px-5 h-[48px] text-[20px] leading-none'
      : size === 'sm'
        ? 'px-3 h-8 text-[13px] leading-none rounded-[8px]'
        : 'px-5 h-[44px] text-sm sm:text-[15px] leading-[22px]';

  const variantClasses =
    {
      primary:
        'bg-primary text-white border border-transparent hover:bg-primary-hover',
      secondary:
        'bg-transparent border border-primary text-primary hover:bg-surface-mint',
      danger:
        'bg-status-error text-white border border-transparent hover:bg-status-error-light',
    }[variant] ||
    'bg-primary text-white border border-transparent hover:bg-primary-hover';

  const content =
    icon == null ? (
      children
    ) : iconPosition === 'start' ? (
      <>
        {icon}
        {children}
      </>
    ) : (
      <>
        {children}
        {icon}
      </>
    );

  return (
    <button
      ref={ref}
      type="button"
      className={`${baseClasses} ${sizeClasses} ${variantClasses} ${className}`}
      {...props}
    >
      {content}
    </button>
  );
});
